import React, { useEffect, useState, useRef } from 'react';
import { SideNavbar } from '../navbar/sideNavbar';
import NotificationBell from '../common/notificationBell';
import { registerPushSubscription } from '../../utils/pushUtils';
import { useUser } from '../../context/userContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './MainLayout.css';
import { JoinGroupsBanner } from '../common/joinGroups';

const MainLayout = ({ children }) => {
    const { user, setUser } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [impersonating, setImpersonating] = useState(false);
    const [adminUsername, setAdminUsername] = useState('');
    /* What the agent still owes on their profile, straight from the server via
       MyRights. The old `profileIncomplete` flag was declared, never set, and gated on
       being ON the profile page - so the banner never appeared, and would have shown in
       the one place it was not needed. (user 2026-10-23: "top banner will ask them to
       fill up profile if not fill up yet") */
    const profileMissing = user?.profileMissing || [];
    const profileIncomplete = profileMissing.length > 0;
    // Viewer-only users have no Dashboard access — top-bar logo routes
    // them to their profile, and the notification bell (showing sales
    // tasks / commission alerts) is hidden entirely.
    const userRoles = user?.roles || [];
    const isViewerOnly = userRoles.length > 0 && userRoles.every(r => r === 'Viewer');
    // A referrer never creates a sale and files no CP58 through us, so the banner's own
    // words have to change — the fields it asks for are the ones we need to PAY them.
    // The gate itself is unchanged: IC, IC photocopy, bank name and account either way.
    const isPureReferral = userRoles.length > 0 && userRoles.every(r => r === 'Referral');
    const homePath = isViewerOnly ? '/user/profile' : '/dashboard';

    useEffect(() => {
        registerPushSubscription();
        const from = localStorage.getItem('ImpersonatingFrom');
        if (from) {
            setImpersonating(true);
            setAdminUsername(from);
        }

        // Nudge an incomplete profile to the profile page - ONCE per session, and never
        // out of the training room. A brand-new agent has no IC or bank details yet, so an
        // unconditional redirect threw them off Tutorials and the Quiz the moment they
        // opened either: "keep on loop redirect, cant stay in a page". (user 2026-09-25)
        //
        // What counts as incomplete now comes from the SERVER (MyRights -> profileMissing),
        // not from a second copy of the rule computed here off agent details. The copy did
        // not know about the IC photocopy, and any rule written twice ends up disagreeing -
        // the banner saying one thing and the gate that actually blocks you saying another.
        if (profileIncomplete) {
            const TRAINING_PATHS = ['/tutorials', '/quiz', '/accept-agreement', '/application-status'];
            const inTraining = TRAINING_PATHS.some(p => location.pathname.startsWith(p));
            let nudged = false;
            try { nudged = sessionStorage.getItem('ProfileNudged') === 'true'; } catch { /* private mode */ }
            if (!nudged && !inTraining && location.pathname !== '/user/profile') {
                try { sessionStorage.setItem('ProfileNudged', 'true'); } catch { /* private mode */ }
                navigate('/user/profile');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.userId, profileIncomplete]);

    // Pull-to-refresh for the iOS/Android home-screen (standalone) app, where the
    // browser's native pull-to-refresh isn't available. Pull down from the top of
    // the scroll area past the threshold to reload.
    const bodyRef = useRef(null);
    const pull = useRef({ startY: 0, active: false });
    const [pullDist, setPullDist] = useState(0);
    const REFRESH_THRESHOLD = 60;

    // A pull that starts on a form control is NOT a pull — it is someone reaching for a
    // field. A reload throws away everything typed into the page, so the gesture has to be
    // unambiguous before it can cost somebody their work. (user 2026-10-06, Bill Analysis:
    // "not sure why auto clear data")
    const isFormTouch = (target) => !!(target && target.closest && target.closest(
        'input, textarea, select, [contenteditable="true"], .MuiInputBase-root, .MuiSelect-root, .MuiSlider-root, .MuiSwitch-root'));

    const onTouchStart = (e) => {
        const el = bodyRef.current;
        if (el && el.scrollTop <= 0 && !isFormTouch(e.target)) {
            pull.current = { startY: e.touches[0].clientY, active: true };
        } else {
            pull.current.active = false;
        }
    };
    const onTouchMove = (e) => {
        if (!pull.current.active) return;
        const dy = e.touches[0].clientY - pull.current.startY;
        if (dy > 0) setPullDist(Math.min(dy * 0.5, 80));
        else setPullDist(0);
    };
    const onTouchEnd = () => {
        if (pull.current.active && pullDist >= REFRESH_THRESHOLD) {
            setPullDist(REFRESH_THRESHOLD);
            window.location.reload();
        } else {
            setPullDist(0);
        }
        pull.current.active = false;
    };

    const handleBackToAdmin = () => {
        const adminToken = localStorage.getItem('AdminToken');
        const adminUserId = localStorage.getItem('AdminUserId');

        if (!adminToken || !adminUserId) return;

        // Restore admin session
        localStorage.setItem('Token', adminToken);
        localStorage.setItem('UserId', adminUserId);
        localStorage.removeItem('AdminToken');
        localStorage.removeItem('AdminUserId');
        localStorage.removeItem('ImpersonatingFrom');

        // Force reload to re-authenticate as admin
        window.location.href = '/dashboard';
    };

    return (
        <div className="main-layout">
            <SideNavbar />
            <div className="main-content">
                {impersonating && (
                    <div className="impersonate-bar">
                        <span>You are viewing as another user</span>
                        <button onClick={handleBackToAdmin}>
                            Back to {adminUsername}
                        </button>
                    </div>
                )}
                <div className="main-header">
                    <div onClick={() => navigate(homePath)} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <img src="/nexwaveLogo.png" alt="Nexwave" style={{ height: 28, marginRight: 8 }} />
                        <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: '#0C1B3A' }}>nexwave</span>
                    </div>
                    <div style={{ flex: 1 }} />
                    {!isViewerOnly && <NotificationBell />}
                </div>
                <div
                    className="main-body"
                    ref={bodyRef}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Pull-to-refresh indicator (home-screen app) */}
                    {pullDist > 0 && (
                        <div style={{
                            height: pullDist, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#64748B', fontFamily: "'Poppins', sans-serif", fontSize: '0.75rem',
                            transition: pull.current.active ? 'none' : 'height 0.2s',
                        }}>
                            {pullDist >= REFRESH_THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
                        </div>
                    )}
                    {/* PROFILE GATE BANNER. Shown on EVERY page while anything is
                        outstanding, because the point is to catch them before they reach
                        the create-sale form - not to tell them off once they are already
                        on the profile. It names the missing fields: sending somebody to a
                        long form with "complete your profile" is how this gets ignored. */}
                    {profileIncomplete && (
                        <div style={{
                            background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10,
                            padding: '12px 20px', marginBottom: 16, display: 'flex', alignItems: 'center',
                            gap: 12, flexWrap: 'wrap', fontFamily: "'Poppins', sans-serif"
                        }}>
                            <div style={{ flex: 1, minWidth: 220 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#92400E' }}>
                                    {isPureReferral
                                        ? 'Complete your profile so we can pay you'
                                        : 'Complete your profile before creating a sale'}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#A16207' }}>
                                    Still needed: {profileMissing.join(', ')}. {isPureReferral
                                        ? 'We need these to pay your referral fee.'
                                        : 'We need these to pay your commission and file your CP58.'}
                                </div>
                            </div>
                            {location.pathname !== '/user/profile' && (
                                <button
                                    onClick={() => navigate('/user/profile')}
                                    style={{
                                        background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 8,
                                        padding: '8px 16px', fontWeight: 700, fontSize: '0.78rem',
                                        fontFamily: "'Poppins', sans-serif", cursor: 'pointer', whiteSpace: 'nowrap',
                                    }}>
                                    Complete profile
                                </button>
                            )}
                        </div>
                    )}
                    {/* One-time welcome — the sales groups, dismissable per user. (user 2026-09-25) */}
                    <JoinGroupsBanner />
                    {children}
                </div>
            </div>
        </div>
    );
};

export default MainLayout;
